import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import ReviewSummary from './ReviewSummary';
import ReviewList from './ReviewList';
import ReviewForm from './ReviewForm';

export interface Review {
  id: number;
  product_id: number;
  user_id: string | null;
  author_name: string;
  rating: number;
  title: string;
  body: string;
  verified_purchase: boolean;
  helpful_count: number;
  created_at: string;
}

interface ReviewSectionProps {
  productId: number;
  productRating: number;
  reviewCount: number;
}

export default function ReviewSection({ productId, productRating, reviewCount }: ReviewSectionProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'highest' | 'lowest' | 'helpful'>('newest');
  const [filterRating, setFilterRating] = useState<number | null>(null);

  const fetchReviews = async () => {
    setLoading(true);
    let query = supabase
      .from('reviews')
      .select('*')
      .eq('product_id', productId);

    if (filterRating) {
      query = query.eq('rating', filterRating);
    }

    switch (sortBy) {
      case 'newest':
        query = query.order('created_at', { ascending: false });
        break;
      case 'highest':
        query = query.order('rating', { ascending: false });
        break;
      case 'lowest':
        query = query.order('rating', { ascending: true });
        break;
      case 'helpful':
        query = query.order('helpful_count', { ascending: false });
        break;
    }

    const { data, error } = await query;
    if (!error && data) {
      setReviews(data);
    } else if (error) {
      console.error('Error fetching reviews:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReviews();
  }, [productId, sortBy, filterRating]);

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count:
      reviews.length > 0 ? reviews.filter((r) => r.rating === star).length : 0,
  }));

  const handleReviewSubmitted = () => {
    setShowForm(false);
    fetchReviews();
  };

  return (
    <section className="mb-12 sm:mb-20" id="reviews">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-6 sm:mb-10">
        <div>
          <h4 className="text-xl sm:text-2xl font-serif text-gray-900 mb-1">
            <a href="#reviews" className="hover:text-[#2E7D32] transition-colors">
              Customer Reviews
            </a>
          </h4>
          <p className="text-xs sm:text-sm text-gray-500">{reviewCount} reviews for this product</p>
        </div>
        <button
          onClick={() => {
            if (!user) {
              navigate('/login');
              return;
            }
            setShowForm(!showForm);
          }}
          className="px-5 sm:px-6 py-2.5 bg-[#2E7D32] text-white text-sm font-medium rounded-lg hover:bg-[#1B5E20] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
          Write a Review
        </button>
      </div>

      {/* Review Form */}
      {showForm && user && (
        <ReviewForm
          productId={productId}
          userId={user.id}
          userName={
            user.user_metadata?.first_name ||
            user.email?.split('@')[0] ||
            'Anonymous'
          }
          onSubmitted={handleReviewSubmitted}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Summary + List */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        <div className="lg:col-span-4">
          <ReviewSummary
            averageRating={productRating}
            totalReviews={reviewCount}
            distribution={ratingDistribution}
            activeFilter={filterRating}
            onFilterChange={(rating) =>
              setFilterRating(rating === filterRating ? null : rating)
            }
          />
        </div>
        <div className="lg:col-span-8">
          <ReviewList
            reviews={reviews}
            loading={loading}
            sortBy={sortBy}
            onSortChange={setSortBy}
            userId={user?.id}
          />
        </div>
      </div>
    </section>
  );
}
